/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Brian Bal, Leonid Khramov
 */
import { generateVideoState } from "../modules/Loader/loadEvent"
import { getProperty } from "trux"

const initialVideo = {
    playlist: null,
    event: null,
    eventId: null,
    playlistEvents: [],
    info: {},
}

const uploadState = {
    gameLoaded: false,
    loggedIn: null,
    online: null,
    creatingGame: false,
    createGameError: null,
    showChooseCamera: false,
    showEditGameTeams: false,
    showVideosProgress: false,
    showAddVideos: false,
    showEditGameInfo: false,
    showRoster: false,
    showWaiting: false,
    showProcessing: false,
    showErrors: false,
    messages: [],
    cameraType: "regular",
    tournamentGames: [],
    team: {
        team_id: -1,
        title: null,
        name: null,
        players: [],
        s3_sources: null,
        abbr: null,
    },
    game: {
        game_id: -1,
        date: "1970-01-01",
        created_user_id: "-1",
        home_team: {
            team_id: null,
            title: null,
            name: null,
            players: [],
            s3_sources: null,
            abbr: null,
            score: 0,
            jersey_color: "#ffffff",
        },
        away_team: {
            team_id: null,
            title: null,
            name: null,
            players: [],
            s3_sources: null,
            abbr: null,
            score: 0,
            jersey_color: "#ffffff",
        },
        field: {
            field_name: null,
            title: null,
            name: null,
            aspect: null,
        },
        creation_time: "1970-01-01 00:00:00",
        first_half_start_time: null,
        first_half_end_time: null,
        second_half_start_time: null,
        second_half_end_time: null,
        flip_field: "1",
        season: null,
        error_message: "",
        status: "unprocessed",
        is_status_processing: false,
        linked_game_id: null,
        trace_case_id: "",
        pi_id: "",
        approx_start_time: "12:00:00",
        approx_half_duration: 45,
        rev: 0,
    },
}

let from = "web"
if (window.sessionStorage) {
    from = window.sessionStorage.getItem("via")
}

const initialState = {
    window: { height: 0, width: 0 },
    via: from ? from : "web",
    isLoggedIn: null,
    isTeamLoggedIn: null,
    toast: "",
    bottomToast: { message: "", show: false, playlistLink: null, link: null, linkText: null, linkState: null },
    allCameras: [],
    allInnings: [],
    selectedCamera: "all",
    selectedHalf: 0,
    selectedInning: "all-innings",
    selectedOrder: "time",
    selectedBaseballSide: "all",
    selectedBaseballTag: "all",
    settings: {
        spotlightEnabled: false,
        muted: true,
    },
    user: {
        loading: false,
        complete: false,
        error: null,
        value: null,
    },
    userProfile: {
        loading: false, 
        complete: false, 
        error: null,
        value: null
    },
    userCollections: {
        loading: false, 
        complete: false, 
        error: null,
        value: []
    },
    whlist: {
        loading: false,
        complete: false,
        error: null,
        value: {},
    },
    team: {
        loading: false,
        complete: false,
        error: null,
        value: null,
    },
    divisions: {
        loading: false,
        complete: false,
        error: null,
        value: false,
    },
    games: {
        loading: false,
        complete: false,
        error: null,
        value: null,
    },
    game: {
        loading: false,
        complete: false,
        error: null,
        value: false,
    },
    stories: {
        loading: false,
        complete: false,
        error: null,
        value: null,
    },
    player_stats: {
        loading: false,
        complete: false,
        error: null,
        value: false,
    },
    player_stats_baseball: {
        loading: false,
        complete: false,
        error: null,
        value: [],
    },
    roster: {
        loading: false,
        complete: false,
        error: null,
        value: false,
    },
    relations: {
        loading: false,
        complete: false,
        error: null,
        value: {},
    },
    family: {
        loading: false,
        complete: false,
        error: null,
        value: []
    },
    familyProfiles: {
        loading: false, 
        complete: false,
        error: null,
        value: []
    },
    video_settings: {},
    video: {
        loading: false,
        complete: false,
        error: null,
        value: { ...initialVideo },
    },
    heatmap: {
        loading: false,
        complete: false,
        error: null,
        value: null,
    },
    team_staff: {
        loading: false,
        complete: false,
        error: null,
        value: {
            team_id: 0,
            staff: [],
        },
    },
    division_staff: {
        loading: false,
        complete: false,
        error: null,
        value: {
            team_id: 0,
            staff: [],
        },
    },
    acl: {
        loading: false,
        complete: false,
        error: null,
        value: {
            team_id: 0,
            staff: [],
        },
    },
    upload: { ...uploadState },
    misc: {
        value: {},
    },
    rev: 0,
}

/**
 * Common state changes like WINDOW SIZE
 * @param state
 * @param action
 * @returns {{} & any & {width, height}}
 */
const trace = (state = null, action) => {
    if (!state) {
        state = JSON.parse(JSON.stringify(initialState))
    }
    let next = Object.assign({}, state)
    let cur, now, reset, game

    switch (action.type) {
        case "WINDOW_SIZE":
            next.window = { height: action.height, width: action.width }
            next.rev += 1
            break
        case "TOAST":
            next.toast = action.message
            break
        case "BOTTOM_TOAST": {
            next.bottomToast = {
                message: action.message, 
                show: action.show,
                playlistLink: action.playlistLink, 
                link: action.link,
                linkText: action.linkText,
                linkState: action.linkState
            }
            break
        }
        case "SET_SPORT_TYPE":
            next.sportType = action.sportType
            if (action.sportType === "soccer") {
                next.selectedOrder = "time"
            }
            break
        case "ALL_CAMERAS":
            next.allCameras = action.allCameras
            break
        case "ALL_INNINGS":
            next.allInnings = action.allInnings
            break
        case "SET_FILTER":
            next[action.key] = action.value
            cur = {
                video: state.video.value,
                game: state.game.value,
            }

            if (
                !(
                    cur.video.playlist === null ||
                    cur.video.eventId === null ||
                    getProperty("video.camera", cur, null) === null
                )
            ) {
                next.video.value = generateVideoState(
                    cur.video.eventId,
                    cur.video.playlist.id,
                    cur.video.camera,
                    cur.game.events,
                    cur.game.playlists,
                    next.sportType,
                    next.selectedHalf,
                    next.selectedInning,
                    next.selectedBaseballSide,
                    next.selectedBaseballTag,
                    next.selectedCamera,
                    next.selectedorder
                )
            }
            break
        case "SELECTED_ORDER":
            next.selectedOrder = action.selectedOrder
            break
        case "SETTINGS_SET":
            next.video_settings = action.video_settings
            break
        case "COMMON_SET":
            cur = state[action.key] ? state[action.key] : action.defaultValue
            now = action.value

            if (cur && action.value && typeof action.value === "object") {
                now = Object.assign({}, cur, action.value)
                if (
                    cur.value &&
                    action.value.value &&
                    !Array.isArray(action.value.value) &&
                    typeof action.value.value === "object"
                ) {
                    now.value = Object.assign({}, cur.value, action.value.value)
                }
            }
            next[action.key] = now
            break
        case "ADD_EVENT":
            console.log("ADD_EVENT")
            if (next?.game?.value?.events) {
                // Add an event number first
                action.value["event_number"] = (state.game.value.events.length + 1)
                // Set the events
                next.game.value.events.push(action.value)
            }
            break
        case "COMMON_RESET":
            console.log("COMMON_RESET")
            next = JSON.parse(JSON.stringify(initialState))
            next.via = state.via
            break
        case "COMMON_GAME_RESET":
            reset = JSON.parse(JSON.stringify(initialState))
            next.game = reset.game
            next.player_stats = reset.player_stats
            next.video = reset.video
            next.heatmap = reset.heatmap
            next.rev += 1
            break
        case "SET_GAME_PROPERTY":
            game = updateGame(state.upload.game, action.key, action.val)
            next.upload.game = game
            break
        case "REV":
            next.rev += 1
            break
        default:
        // nothing to do but keeping lint happy
    }

    // Update is logged in
    let isLoggedIn = null
    if (state.user.value) {
        isLoggedIn = true
    } else if (state.user.error) {
        isLoggedIn = false
    }
    next.isLoggedIn = isLoggedIn

    // update rev
    next.rev += 1

    return next
}

function updateGame(game, key, val) {
    var parts = key.split(".")

    var orig = Object.assign({}, game)
    var obj = orig
    for (var i = 0; i < parts.length - 1; i++) {
        var part = parts[i]
        obj = obj[part]
        // eslint-disable-next-line eqeqeq
        if (obj == null) {
            console.warn("updateGame", "key not found", game, key, val)
            return game
        }
    }
    let p = parts[parts.length - 1]
    if (typeof obj[p] === "object" && typeof val === "object") {
        obj[p] = { ...obj[p], ...val }
    } else {
        obj[p] = val
    }

    return orig
}

export default trace
